import {
  Box, Button, Dialog, DialogContent, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import BarraSemaforo from './BarraSemaforo';
import StatusChip from './StatusChip';
import {
  COR, STATUS, STATUS_NAO_COBRADA, faltaLiquido, formatarData, formatarPercentual, formatarReal, resumir, rotuloCompetencia, situacaoContrato, textoPrazo,
} from './financeiroUtils';

const cabecalho = { fontSize: 12.5, fontWeight: 600, color: COR.texto2, whiteSpace: 'nowrap', py: 1.25 };
const numero = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function Indicador({ rotulo, valor, detalhe, cor = COR.ink }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, color: COR.texto2 }}>{rotulo}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>
        {valor}
      </Typography>
      {detalhe && <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>{detalhe}</Typography>}
    </Box>
  );
}

export default function ProjetoNFDialog({ projeto, podeGerenciar, onFechar, onNova, onEditar, onExcluir, onAbrirNF, onEditarContrato }) {
  const telaPequena = useMediaQuery('(max-width:700px)');
  const receitas = projeto?.receitas || [];
  // Notas de projeto cancelado que ainda não foram recebidas não entram nos totais (ver financeiroUtils).
  const validas = receitas.filter((rec) => !rec.nao_cobrada);
  const qtdNaoCobradas = receitas.length - validas.length;
  const r = resumir(validas);
  const valores = Object.fromEntries(Object.entries(r.porStatus).map(([s, v]) => [s, v.valor]));
  const contrato = projeto ? situacaoContrato(projeto, r.faturado) : { informado: false };
  const ehContrato = Boolean(projeto?.eh_contrato);
  const projetosDoContrato = projeto?.projetos || [];
  const unidade = ehContrato ? 'contrato' : 'projeto';

  return (
    <Dialog open={Boolean(projeto)} onClose={onFechar} maxWidth="lg" fullWidth fullScreen={telaPequena} scroll="paper" slotProps={{ paper: { sx: { '& .MuiButton-root': { textTransform: 'none', fontWeight: 600 } } } }}>
      {projeto && (
        <>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2, borderBottom: `1px solid ${COR.borda}`, position: 'relative' }}>
            <IconButton aria-label="Fechar" onClick={onFechar} sx={{ position: 'absolute', right: 12, top: 12 }}>
              <CloseRoundedIcon />
            </IconButton>
            <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700, color: COR.ink, pr: 6, lineHeight: 1.3 }}>
              {projeto.nome}
            </Typography>
            <Typography sx={{ fontSize: 14, color: COR.texto2 }}>
              {projeto.cliente_nome}
              {ehContrato && (
                projetosDoContrato.length
                  ? ` · Contrato com ${projetosDoContrato.length} ${projetosDoContrato.length === 1 ? 'projeto' : 'projetos'}: ${projetosDoContrato.map((p) => p.nome).join(', ')}`
                  : ' · Contrato aguardando projeto'
              )}
            </Typography>

            <Box
              sx={{
                display: 'grid', gap: { xs: 2, sm: 4 }, mt: 2.5,
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, auto) 1fr' },
                alignItems: 'start',
              }}
            >
              <Indicador rotulo="Faturado" valor={formatarReal(r.faturado)} detalhe={`${r.qtd} ${r.qtd === 1 ? 'nota' : 'notas'}${qtdNaoCobradas ? ` · ${qtdNaoCobradas} não ${qtdNaoCobradas === 1 ? 'será cobrada' : 'serão cobradas'}` : ''}`} />
              <Indicador rotulo="Recebido (líquido)" valor={formatarReal(r.recebido)} detalhe={`${formatarPercentual(r.pctQuitado)} do faturado quitado`} cor={COR.navy} />
              <Indicador rotulo="Em aberto" valor={formatarReal(r.aberto)} detalhe="Notas ainda não recebidas" />
              <Indicador
                rotulo="Vencido" valor={formatarReal(r.vencido)} cor={r.vencido ? STATUS.em_aberto.texto : COR.ink}
                detalhe={r.porStatus.em_aberto.qtd ? `${r.porStatus.em_aberto.qtd} ${r.porStatus.em_aberto.qtd === 1 ? 'nota' : 'notas'} sem recebimento` : 'Nenhuma nota vencida'}
              />
              <Box sx={{ gridColumn: { xs: 'span 2', md: 'auto' }, alignSelf: 'center', minWidth: 160 }}>
                <BarraSemaforo valores={valores} altura={10} animar={false} />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid', gap: { xs: 2, sm: 4 }, mt: 2.5, pt: 2.25, borderTop: `1px solid ${COR.borda}`,
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(2, auto) 1fr' }, alignItems: 'center',
              }}
            >
              <Indicador
                rotulo="Valor do contrato"
                valor={contrato.informado ? formatarReal(contrato.contrato) : 'Não informado'}
                cor={contrato.informado ? COR.ink : STATUS.a_receber.texto}
                detalhe={contrato.informado ? `Total vendido neste ${unidade}` : 'O Comercial ainda não informou'}
              />
              {contrato.informado ? (
                <Indicador
                  rotulo={contrato.estourou ? 'Faturado acima do contrato' : 'Saldo a faturar'}
                  valor={formatarReal(Math.abs(contrato.saldo))}
                  cor={contrato.estourou ? STATUS.em_aberto.texto : COR.ink}
                  detalhe={contrato.estourou ? 'As NFs somam mais que o contrato' : 'Contrato menos o faturado'}
                />
              ) : <Box />}
              <Box sx={{ gridColumn: { xs: 'span 2', md: 'auto' }, minWidth: 180 }}>
                {contrato.informado && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>Contrato faturado</Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: contrato.estourou ? STATUS.em_aberto.texto : COR.ink, fontVariantNumeric: 'tabular-nums' }}>
                        {formatarPercentual(contrato.pctFaturado)}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 4, backgroundColor: '#e8edf3', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${Math.min(100, contrato.pctFaturado)}%`, borderRadius: 4, backgroundColor: contrato.estourou ? STATUS.em_aberto.cor : COR.navy }} />
                    </Box>
                  </Box>
                )}
                {podeGerenciar && (
                  <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => onEditarContrato(projeto)} sx={{ px: 0.5, color: COR.navy }}>
                    {contrato.informado ? 'Alterar valor do contrato' : 'Informar valor do contrato'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
            {podeGerenciar && (
              <Button
                variant="contained" startIcon={<AddRoundedIcon />} onClick={onNova}
                sx={{ backgroundColor: COR.navy, mb: 2, ml: { xs: 1, sm: 0 }, '&:hover': { backgroundColor: COR.ink } }}
              >
                Lançar NF
              </Button>
            )}

            {(projeto.status === 'cancelado' || qtdNaoCobradas > 0) && (
              <Box
                role="status"
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, mx: { xs: 1, sm: 0 }, px: 1.5, py: 1, borderRadius: 2,
                  backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20, color: '#991B1B', mt: '1px' }} />
                <Typography sx={{ fontSize: 13.5, color: '#7F1D1D' }}>
                  <strong>
                    {projeto.status === 'cancelado'
                      ? (ehContrato ? 'Todos os projetos deste contrato foram cancelados.' : 'Projeto cancelado.')
                      : 'Este contrato tem projeto cancelado.'}
                  </strong>
                  {' '}As notas ainda não recebidas {projeto.status === 'cancelado' ? '' : 'desse projeto '}não serão cobradas e não entram nos totais do Financeiro.
                  {' '}Se o projeto for reativado, elas voltam a valer.
                </Typography>
              </Box>
            )}

            {receitas.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: COR.texto2 }}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontWeight: 600, color: COR.ink }}>Nenhuma nota lançada neste {unidade}</Typography>
                <Typography sx={{ fontSize: 14 }}>
                  {podeGerenciar ? 'Clique em Lançar NF para registrar a primeira.' : 'Quando houver lançamentos, eles aparecem aqui.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ border: `1px solid ${COR.borda}`, borderRadius: 2 }}>
                <Table size="small" sx={{ minWidth: ehContrato ? 1060 : 940 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: COR.fundo }}>
                      <TableCell sx={cabecalho}>Competência</TableCell>
                      <TableCell sx={cabecalho}>Nota fiscal</TableCell>
                      {ehContrato && <TableCell sx={cabecalho}>Projeto</TableCell>}
                      <TableCell sx={cabecalho} align="right">Valor da NF</TableCell>
                      <TableCell sx={cabecalho} align="right">Líquido recebido</TableCell>
                      <TableCell sx={cabecalho}>Vencimento</TableCell>
                      <TableCell sx={cabecalho}>Recebimento</TableCell>
                      <TableCell sx={cabecalho}>Situação</TableCell>
                      <TableCell sx={cabecalho} align="right">{podeGerenciar ? 'Ações' : 'NF'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receitas.map((rec) => {
                      const naoCobrada = Boolean(rec.nao_cobrada);
                      const vencida = rec.status === 'em_aberto' && !naoCobrada;
                      return (
                        <TableRow
                          key={rec.id}
                          sx={{
                            backgroundColor: vencida ? '#FFF6F6' : (naoCobrada ? '#FAFAFA' : 'transparent'),
                            '&:last-child td': { borderBottom: 0 },
                            '& td': { py: 1.25, verticalAlign: 'top' },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{rotuloCompetencia(rec.mes, rec.ano)}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: rec.numero_nf ? COR.ink : COR.texto2 }}>
                              {rec.numero_nf ? `NF ${rec.numero_nf}` : 'Sem número'}
                            </Typography>
                            {rec.numero_parcela ? (
                              <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>
                                Parcela {rec.numero_parcela}{rec.total_parcelas ? ` de ${rec.total_parcelas}` : ''}
                              </Typography>
                            ) : null}
                          </TableCell>
                          {ehContrato && (
                            <TableCell sx={{ fontSize: 14, color: rec.projeto_nome ? COR.ink : COR.texto2 }}>
                              {rec.projeto_nome || 'Contrato inteiro'}
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ ...numero, fontWeight: 600, ...(naoCobrada ? { textDecoration: 'line-through', color: '#94a3b8' } : {}) }}>{formatarReal(rec.valor)}</TableCell>
                          <TableCell align="right" sx={numero}>
                            {faltaLiquido(rec) ? (
                              <Tooltip title={podeGerenciar ? 'Nota recebida sem valor líquido. Clique para informar.' : 'Nota recebida sem valor líquido informado.'}>
                                <Box
                                  component={podeGerenciar ? 'button' : 'span'} type={podeGerenciar ? 'button' : undefined}
                                  onClick={podeGerenciar ? () => onEditar(rec) : undefined}
                                  sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '999px',
                                    border: `1px solid ${STATUS.a_receber.cor}`, backgroundColor: STATUS.a_receber.fundo,
                                    color: STATUS.a_receber.texto, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                                    cursor: podeGerenciar ? 'pointer' : 'default',
                                  }}
                                >
                                  <WarningAmberRoundedIcon sx={{ fontSize: 15 }} /> Informar líquido
                                </Box>
                              </Tooltip>
                            ) : rec.valor_liquido_recebido !== null && rec.valor_liquido_recebido !== undefined
                              ? formatarReal(rec.valor_liquido_recebido)
                              : <Box component="span" sx={{ color: '#94a3b8' }}>—</Box>}
                          </TableCell>
                          <TableCell sx={numero}>{formatarData(rec.data_vencimento) || <Box component="span" sx={{ color: '#94a3b8' }}>—</Box>}</TableCell>
                          <TableCell sx={numero}>{formatarData(rec.data_recebimento) || <Box component="span" sx={{ color: '#94a3b8' }}>—</Box>}</TableCell>
                          <TableCell>
                            {naoCobrada ? (
                              <>
                                <Tooltip title={`${STATUS_NAO_COBRADA.descricao}. Se o projeto for reativado, a nota volta a valer.`}>
                                  <span><StatusChip status="nao_cobrada" pequeno /></span>
                                </Tooltip>
                                <Typography sx={{ fontSize: 12, mt: 0.5, color: COR.texto2 }}>
                                  {rec.data_vencimento ? `Venceria em ${formatarData(rec.data_vencimento)}` : 'Sem vencimento informado'}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <StatusChip
                                  status={rec.status} pequeno
                                  corrigido={Boolean(rec.status_manual)} automatico={rec.status_automatico}
                                />
                                <Typography sx={{ fontSize: 12, mt: 0.5, color: vencida ? STATUS.em_aberto.texto : COR.texto2, fontWeight: vencida ? 600 : 400 }}>
                                  {textoPrazo({ status: rec.status, dias: rec.dias_para_vencer, data_recebimento: rec.data_recebimento })}
                                </Typography>
                              </>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {rec.tem_arquivo_nf ? (
                              <Tooltip title={`Abrir ${rec.arquivo_nf_nome}`}>
                                <IconButton size="small" aria-label="Abrir NF anexada" onClick={() => onAbrirNF(rec)} sx={{ color: COR.navy }}>
                                  <AttachFileRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              podeGerenciar && (
                                <Tooltip title="Sem NF anexada. Clique em editar para anexar.">
                                  <Box component="span" sx={{ display: 'inline-block', width: 30 }} />
                                </Tooltip>
                              )
                            )}
                            {podeGerenciar && (
                              <>
                                <Tooltip title="Editar lançamento">
                                  <IconButton size="small" aria-label="Editar lançamento" onClick={() => onEditar(rec)}>
                                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir lançamento">
                                  <IconButton size="small" aria-label="Excluir lançamento" onClick={() => onExcluir(rec)} sx={{ '&:hover': { color: STATUS.em_aberto.cor } }}>
                                    <DeleteOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
